/**
 * RiskDropdown Constants
 *
 * This file contains constants and utility functions for risk dropdowns.
 * Separated from component files to avoid React Fast Refresh warnings.
 *
 * Note: This file intentionally exports only constants and utility functions,
 * not React components. The react-refresh warning is disabled.
 */

/* eslint-disable react-refresh/only-export-components */

import {
  Bars2Icon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { CircleCheck, LockKeyholeOpen, RefreshCcwDot } from 'lucide-react';

import { RiskStatus } from '@/types';
import { getAvailableStatuses } from '@/utils/availableStatuses.util';
import {
  getStatusLabel,
  type VulnerabilityStatusMode,
} from '@/utils/vulnerabilityStatus.util';

/**
 * Get all risk status options including Triaged with mode-aware labels
 * Used as the source of truth for status configurations
 *
 * @param mode - The current vulnerability status mode (legacy or new)
 * @returns Array of all status options with mode-aware labels
 */
function getAllRiskStatusOptions(mode: VulnerabilityStatusMode) {
  return [
    {
      label: getStatusLabel(RiskStatus.Triaged, mode),
      value: RiskStatus.Triaged,
      icon: <ClockIcon className="size-4" />,
      count: '⌥ T',
    },
    {
      label: getStatusLabel(RiskStatus.Opened, mode),
      value: RiskStatus.Opened,
      icon: <LockKeyholeOpen className="size-4" />,
      count: '⌥ O',
    },
    {
      label: getStatusLabel(RiskStatus.Remediated, mode),
      value: RiskStatus.Remediated,
      icon: <RefreshCcwDot className="size-4" />,
      count: '⌥ C',
    },
    {
      label: getStatusLabel(RiskStatus.Accepted, mode),
      value: RiskStatus.Accepted,
      icon: <CircleCheck className="size-4" />,
      count: '⌥ A',
    },
  ];
}

/**
 * Get base risk status options filtered by user type and mode with mode-aware labels
 *
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - The current vulnerability status mode (default: 'legacy')
 * @returns Array of status options with icons and keyboard shortcuts
 *
 * @example
 * ```typescript
 * // For customer users in legacy mode (excludes Triaged)
 * const customerLegacyOptions = getBaseRiskStatusOptions(false, 'legacy');
 *
 * // For customer users with new vulnerability system (includes Triaged as "Detected")
 * const customerNewOptions = getBaseRiskStatusOptions(false, 'new');
 *
 * // For Praetorian users (includes Triaged in any mode)
 * const praetorianOptions = getBaseRiskStatusOptions(true, 'new');
 * ```
 */
export function getBaseRiskStatusOptions(
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
) {
  const availableStatuses = getAvailableStatuses(isPraetorianUser, mode);
  const allOptions = getAllRiskStatusOptions(mode);

  return allOptions.filter(option =>
    availableStatuses.includes(option.value as RiskStatus)
  );
}

/**
 * @deprecated Use getBaseRiskStatusOptions(false, mode) instead
 * Kept for backward compatibility with existing code
 * Defaults to customer mode (excludes Triaged) and legacy labels
 */
export const BaseRiskStatusOptions = getBaseRiskStatusOptions(false, 'legacy');

export const riskSeverityOptions = [
  {
    label: 'Critical',
    value: 'C',
    icon: <ChevronDoubleUpIcon className="size-4 stroke-2" />,
  },
  {
    label: 'High',
    value: 'H',
    icon: <ChevronUpIcon className="size-4 stroke-2" />,
  },
  {
    label: 'Medium',
    value: 'M',
    icon: <Bars2Icon className="size-4 stroke-2" />,
  },
  {
    label: 'Low',
    value: 'L',
    icon: <ChevronDownIcon className="size-4 stroke-2" />,
  },
  {
    label: 'Info',
    value: 'I',
    icon: <ChevronDoubleDownIcon className="size-4" />,
  },
];
